import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCamera } from '@pella/barcode-scanner';
import { toast } from '@pella/sash-framework';
import { useTranslation } from 'react-i18next';
import { useThrive } from '../useThrive';

/**
 * Parses the equipment ID from a scanner URL.
 * @param {string} url - The scanned URL string containing query parameters.
 * @returns {string | null} The extracted equipment ID value, or null if not present.
 */
const parseURL = (url) => {
  try {
    return new URL(url).searchParams.get('equip-id');
  } catch {
    return null;
  }
};

/**
 * Hook for managing equipment selection via the `equip-id` URL query param.
 * The URL is the single source of truth: the barcode scanner and reset flow
 * both update the URL via setSearchParams, which drives the equipment lookup.
 * @returns {object} Equipment state and handlers
 */
export const useEquipment = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { startCamera } = useCamera();
  const { t } = useTranslation();
  const { useEquipmentByNumber } = useThrive();

  const queryStringEquipID = searchParams.get('equip-id');

  const { equipment: fetchedEquipment, equipmentIsError } = useEquipmentByNumber(queryStringEquipID);

  const selectedEquipment = fetchedEquipment ?? null;
  const scanAgainVisible = !selectedEquipment;

  // Toast on query error — legitimate effect (side-effect synced to external toast system)
  const prevIsErrorRef = useRef(false);
  useEffect(() => {
    if (equipmentIsError && !prevIsErrorRef.current) {
      toast.error(t('equipmentNotFoundContent'));
    }
    prevIsErrorRef.current = equipmentIsError;
  }, [equipmentIsError, t]);

  const writeEquipId = (equipNum) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (equipNum === null) {
          next.delete('equip-id');
        } else {
          next.set('equip-id', equipNum);
        }
        return next;
      },
      { replace: true },
    );
  };

  const handleScannerRead = (value) => {
    const equipNum = parseURL(value);
    writeEquipId(equipNum);
    toast.info(`${t('scannedValue')}: ${equipNum}`);
  };

  const handleScanAgain = () => startCamera(handleScannerRead);

  const reset = () => {
    writeEquipId(null);
  };

  return { selectedEquipment, scanAgainVisible, handleScanAgain, reset };
};
